import React, { useState, useEffect } from 'react';
import PasswordInput from '../shared/PasswordInput';
import { adminApi } from '../../services/api';
import { validatePassword, PASSWORD_REQUIREMENTS } from '../../utils/passwordValidation';

const SalesUserEditor = ({ salesUser, roles, salesTeams = [], onSave, onAlert }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [membershipRoles, setMembershipRoles] = useState([]);
  const [allSalesTeams, setAllSalesTeams] = useState([]);
  const [showAddMembership, setShowAddMembership] = useState(false);
  const [newMembership, setNewMembership] = useState({ organization_id: '', role_id: '', sales_team_id: '', notes: '' });
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [confirmPasswordChange, setConfirmPasswordChange] = useState(false);


  useEffect(() => {
    setEditedData({
      first_name: salesUser.user?.first_name || '',
      last_name: salesUser.user?.last_name || '',
      email: salesUser.user?.email || '',
      username: salesUser.user?.username || '',
      password: '',
      role_id: salesUser.role?.id || '',
      sales_team_id: salesUser.sales_team?.id || '',
      territory: salesUser.territory || '',
      region: salesUser.region || '',
      phone: salesUser.phone || '',
      phone_2: salesUser.phone_2 || '',
      email_2: salesUser.email_2 || '',
      street_address: salesUser.street_address || '',
      street_address_2: salesUser.street_address_2 || '',
      city: salesUser.city || '',
      state: salesUser.state || '',
      zip_code: salesUser.zip_code || '',
      quota_amount: salesUser.quota_amount || '',
      quota_period: salesUser.quota_period || 'monthly',
      commission_rate: salesUser.commission_rate || '',
      employee_id: salesUser.employee_id || '',
      work_location: salesUser.work_location || 'remote',
      office_location: salesUser.office_location || '',
      start_date: salesUser.start_date || '',
      is_active: salesUser.is_active
    });
  }, [salesUser]);

  useEffect(() => {
    fetchOrganizationsAndRoles();
  }, []);

  // Fetch organization-specific teams and roles when organization is selected
  useEffect(() => {
    if (newMembership.organization_id && showAddMembership) {
      fetchTeamsForOrganization(newMembership.organization_id);
      fetchRolesForOrganization(newMembership.organization_id);
    } else {
      setAllSalesTeams([]);
      setMembershipRoles([]);
    }
  }, [newMembership.organization_id, showAddMembership]);

  const fetchOrganizationsAndRoles = async () => {
    try {
      const orgsRes = await adminApi.getOrganizations({ is_active: 'true' });
      setOrganizations(orgsRes.data?.organizations || []);
    } catch (err) {
      onAlert?.({ type: 'error', title: 'Failed to load organizations', message: err.response?.data?.message || err.message });
    }
  };

  const fetchRolesForOrganization = async (orgId) => {
    try {
      // Backend returns both global and org-specific roles when org ID is provided
      const rolesRes = await adminApi.getRoles({ category: 'org_user', is_active: 'true', organization: orgId });
      setMembershipRoles(rolesRes.data?.roles || []);
    } catch (err) {
      onAlert?.({ type: 'error', title: 'Failed to load roles', message: err.response?.data?.message || err.message });
    }
  };

  const fetchTeamsForOrganization = async (orgId) => {
    try {
      // Fetch teams filtered by organization_id from backend
      const teamsRes = await adminApi.getSalesTeams({ organization_id: orgId, is_active: 'true' });
      setAllSalesTeams(teamsRes.data?.teams || []);
    } catch (err) {
      onAlert?.({ type: 'error', title: 'Failed to load teams', message: err.response?.data?.message || err.message });
      setAllSalesTeams([]);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Validate password if provided
      if (editedData.password && editedData.password.trim() !== '') {
        const passwordValidation = validatePassword(editedData.password);
        if (!passwordValidation.isValid) {
          onAlert?.({ type: 'error', title: 'Validation Error', message: passwordValidation.error });
          setSaving(false);
          return;
        }
        if (!confirmPasswordChange) {
          onAlert?.({ type: 'error', title: 'Confirmation Required', message: 'Please confirm the password change by checking the confirmation box' });
          setSaving(false);
          return;
        }
      }

      // Prepare payload - exclude password if empty or not confirmed
      const payload = { ...editedData };
      if (!payload.password || payload.password.trim() === '' || !confirmPasswordChange) {
        delete payload.password;
      }
      await adminApi.updateSalesUser(salesUser.id, payload);
      setIsEditMode(false);
      setConfirmPasswordChange(false);
      onAlert?.({ type: 'success', title: '', message: 'User updated successfully' });
      if (onSave) onSave();
    } catch (err) {
      onAlert?.({ type: 'error', title: 'Failed to update user', message: err.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleAddMembership = async () => {
    if (!newMembership.organization_id || !newMembership.role_id) {
      onAlert?.({ type: 'error', title: 'Validation Error', message: 'Please select an organization and role' });
      return;
    }

    try {
      setMembershipLoading(true);
      const payload = {
        user_id: salesUser.user.id,
        organization_id: newMembership.organization_id,
        role_id: newMembership.role_id,
        notes: newMembership.notes
      };
      // Only add sales_team_id if it's not empty
      if (newMembership.sales_team_id) {
        payload.sales_team_id = newMembership.sales_team_id;
      }
      await adminApi.createOrganizationMembership(payload);
      setShowAddMembership(false);
      setNewMembership({ organization_id: '', role_id: '', sales_team_id: '', notes: '' });
      onAlert?.({ type: 'success', title: '', message: 'Membership added successfully' });
      if (onSave) onSave();
    } catch (err) {
      onAlert?.({ type: 'error', title: 'Failed to add membership', message: err.response?.data?.message || err.message });
    } finally {
      setMembershipLoading(false);
    }
  };

  const handleRemoveMembership = async (membershipId) => {
    if (!window.confirm('Are you sure you want to remove this organization membership?')) {
      return;
    }

    try {
      setMembershipLoading(true);
      await adminApi.deleteOrganizationMembership(membershipId);
      onAlert?.({ type: 'success', title: '', message: 'Membership removed successfully' });
      if (onSave) onSave();
    } catch (err) {
      onAlert?.({ type: 'error', title: 'Failed to remove membership', message: err.response?.data?.message || err.message });
    } finally {
      setMembershipLoading(false);
    }
  };

  return (
    <div>
      <div className="card-title d-flex justify-content-between align-items-center mb-3">
        <h5 className="text-primary mb-0">{salesUser.user?.first_name} {salesUser.user?.last_name}</h5>
        {!isEditMode && (
          <button className="btn btn-sm btn-outline-primary" onClick={() => setIsEditMode(true)}>
            <i className="fas fa-pencil me-1" />Edit
          </button>
        )}
      </div>
      <div className="row">
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">First Name:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.first_name} onChange={e => setEditedData({...editedData, first_name: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.user?.first_name || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Last Name:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.last_name} onChange={e => setEditedData({...editedData, last_name: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.user?.last_name || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Email:</label>
          {isEditMode ? (
            <input type="email" className="form-control form-control-sm" value={editedData.email} onChange={e => setEditedData({...editedData, email: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.user?.email || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Username:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.username} onChange={e => setEditedData({...editedData, username: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.user?.username || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Role:</label>
          {isEditMode ? (
            <select className="form-select form-select-sm" value={editedData.role_id} onChange={e => setEditedData({...editedData, role_id: e.target.value})}>
              <option value="">Select Role</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          ) : (
            <div className="text-dark">{salesUser.role?.name || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Sales Team:</label>
          {isEditMode ? (
            <select className="form-select form-select-sm" value={editedData.sales_team_id} onChange={e => setEditedData({...editedData, sales_team_id: e.target.value})}>
              <option value="">No Team Assigned</option>
              {salesTeams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
            </select>
          ) : (
            <div className="text-dark">{salesUser.sales_team?.name || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          {isEditMode ? (
            <>
              <PasswordInput
                value={editedData.password}
                onChange={e => { setEditedData({...editedData, password: e.target.value}); setConfirmPasswordChange(false); }}
                required={false}
                label="Password"
                placeholder="Leave blank to keep current"
                showRequirements
                className=""
              />
              {editedData.password && editedData.password.trim() !== '' && (
                <div className="form-check mt-2">
                  <input type="checkbox" className="form-check-input" id="confirmPasswordChange" checked={confirmPasswordChange} onChange={e => setConfirmPasswordChange(e.target.checked)} />
                  <label className="form-check-label small text-muted" htmlFor="confirmPasswordChange">
                    I want to update the user&apos;s password. This will not affect their existing sessions. They can continue using Ankr till next login.
                  </label>
                </div>
              )}
            </>
          ) : (
            <>
              <label className="form-label small fw-bold text-muted mb-1">Password:</label>
              <div className="text-dark">••••••••</div>
            </>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Territory:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.territory} onChange={e => setEditedData({...editedData, territory: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.territory || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Region:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.region} onChange={e => setEditedData({...editedData, region: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.region || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Phone:</label>
          {isEditMode ? (
            <input type="tel" className="form-control form-control-sm" value={editedData.phone} onChange={e => setEditedData({...editedData, phone: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.phone || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Phone 2:</label>
          {isEditMode ? (
            <input type="tel" className="form-control form-control-sm" value={editedData.phone_2} onChange={e => setEditedData({...editedData, phone_2: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.phone_2 || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Email 2:</label>
          {isEditMode ? (
            <input type="email" className="form-control form-control-sm" value={editedData.email_2} onChange={e => setEditedData({...editedData, email_2: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.email_2 || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Street Address:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.street_address} onChange={e => setEditedData({...editedData, street_address: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.street_address || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Street Address 2:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.street_address_2} onChange={e => setEditedData({...editedData, street_address_2: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.street_address_2 || '-'}</div>
          )}
        </div>
        <div className="col-4 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">City:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.city} onChange={e => setEditedData({...editedData, city: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.city || '-'}</div>
          )}
        </div>
        <div className="col-4 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">State:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.state} onChange={e => setEditedData({...editedData, state: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.state || '-'}</div>
          )}
        </div>
        <div className="col-4 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Zip Code:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.zip_code} onChange={e => setEditedData({...editedData, zip_code: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.zip_code || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Quota Amount:</label>
          {isEditMode ? (
            <input type="number" className="form-control form-control-sm" value={editedData.quota_amount} onChange={e => setEditedData({...editedData, quota_amount: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.quota_amount || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Quota Period:</label>
          {isEditMode ? (
            <select className="form-select form-select-sm" value={editedData.quota_period} onChange={e => setEditedData({...editedData, quota_period: e.target.value})}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annually">Annually</option>
            </select>
          ) : (
            <div className="text-dark">{salesUser.quota_period || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Commission Rate (%):</label>
          {isEditMode ? (
            <input type="number" step="0.01" className="form-control form-control-sm" value={editedData.commission_rate} onChange={e => setEditedData({...editedData, commission_rate: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.commission_rate ? `${salesUser.commission_rate}%` : '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Employee ID:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.employee_id} onChange={e => setEditedData({...editedData, employee_id: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.employee_id || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Work Location:</label>
          {isEditMode ? (
            <select className="form-select form-select-sm" value={editedData.work_location} onChange={e => setEditedData({...editedData, work_location: e.target.value})}>
              <option value="remote">Remote</option>
              <option value="office">Office</option>
              <option value="hybrid">Hybrid</option>
            </select>
          ) : (
            <div className="text-dark">{salesUser.work_location || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Office Location:</label>
          {isEditMode ? (
            <input type="text" className="form-control form-control-sm" value={editedData.office_location} onChange={e => setEditedData({...editedData, office_location: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.office_location || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          <label className="form-label small fw-bold text-muted mb-1">Start Date:</label>
          {isEditMode ? (
            <input type="date" className="form-control form-control-sm" value={editedData.start_date} onChange={e => setEditedData({...editedData, start_date: e.target.value})} />
          ) : (
            <div className="text-dark">{salesUser.start_date || '-'}</div>
          )}
        </div>
        <div className="col-6 mb-3">
          {isEditMode ? (
            <div className="form-check">
              <input type="checkbox" className="form-check-input" checked={editedData.is_active} onChange={e => setEditedData({...editedData, is_active: e.target.checked})} />
              <label className="form-check-label small">{editedData.is_active ? 'Active' : 'Inactive'}</label>
            </div>
          ) : (
            <span className={`badge bg-${salesUser.is_active ? 'success' : 'secondary'}`}>{salesUser.is_active ? 'Active' : 'Inactive'}</span>
          )}
        </div>
        {isEditMode && (<div>
            <button className="btn btn-primary me-2" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</> : <>Update User</>}
            </button>
            <button className="btn btn-secondary" onClick={() => { setIsEditMode(false); setConfirmPasswordChange(false); }} disabled={saving}>
              Cancel
            </button>
        </div>)}
      </div>

      <hr className="my-3" />

      {/* Organization Memberships Section */}
      <div className="card-body p-0 mb-3">
        <div className="card-title d-flex justify-content-between align-items-center mb-2">
          <h6 className="text-primary mb-0 fw-bold">Organization Memberships</h6>
          {!showAddMembership && (
            <button className="btn btn-sm btn-outline-primary" onClick={() => setShowAddMembership(true)} disabled={membershipLoading}>
              <i className="fas fa-plus me-1" />Add
            </button>
          )}
        </div>

        {/* Add Membership Form */}
        {showAddMembership && (
          <div className="card mb-3">
            <div className="card-body p-2">
              <div className="row g-2">
                <div className="col-12">
                  <label className="form-label small mb-1">Organization *</label>
                  <select className="form-select form-select-sm" value={newMembership.organization_id} onChange={e => setNewMembership({...newMembership, organization_id: e.target.value, sales_team_id: ''})}>
                    <option value="">Select Organization</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label small mb-1">Role *</label>
                  <select className="form-select form-select-sm" value={newMembership.role_id} onChange={e => setNewMembership({...newMembership, role_id: e.target.value})}>
                    <option value="">Select Role</option>
                    {membershipRoles.map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label small mb-1">Sales Team (Optional)</label>
                  <select
                    className="form-select form-select-sm"
                    value={newMembership.sales_team_id}
                    onChange={e => setNewMembership({...newMembership, sales_team_id: e.target.value})}
                    disabled={!newMembership.organization_id}
                  >
                    <option value="">No Team</option>
                    {allSalesTeams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                  {!newMembership.organization_id ? (
                    <small className="text-muted">Select an organization first to see available teams</small>
                  ) : allSalesTeams.length === 0 ? (
                    <small className="text-warning">No teams available for this organization</small>
                  ) : null}
                </div>
                <div className="col-12">
                  <label className="form-label small mb-1">Notes</label>
                  <textarea className="form-control form-control-sm" rows="2" value={newMembership.notes} onChange={e => setNewMembership({...newMembership, notes: e.target.value})} placeholder="Optional notes about this membership" />
                </div>
                <div className="col-12">
                    <button className="btn btn-sm btn-primary me-2" onClick={handleAddMembership} disabled={membershipLoading}>
                      {membershipLoading ? <><span className="spinner-border spinner-border-sm me-1" />Adding...</> : <>Add Membership</>}
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={() => { setShowAddMembership(false); setNewMembership({ organization_id: '', role_id: '', sales_team_id: '', notes: '' }); }} disabled={membershipLoading}>
                      Cancel
                    </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Existing Memberships List */}
        {salesUser.organization_memberships?.length > 0 ? (
          <div className="list-group">
            {salesUser.organization_memberships.map((membership) => (
              <div key={membership.id} className="list-group-item list-group-item-action p-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="fw-bold small">{membership.organization?.name}</div>
                    <div className="small text-muted">
                      <span className="badge bg-info me-1">{membership.role?.name || 'No Role'}</span>
                      {salesUser.sales_team && (
                        <span className="badge bg-success me-1">
                          <i className="fas fa-users me-1" />
                          {salesUser.sales_team.name}
                        </span>
                      )}

                {/* <hr className="my-3" />

                <div className="row g-2 small">
                  {selectedUser.sales_team && (
                    <div className="col-12">
                      <h6 className="mb-1">Team:</h6>
                      <RelationshipBadge label={selectedUser.sales_team.name} icon="fa-users" variant="primary" />
                    </div>
                  )}
                  {selectedUser.manager && (
                    <div className="col-12 mt-2">
                      <h6 className="mb-1">Manager:</h6>
                      <RelationshipBadge label={`${selectedUser.manager.user?.first_name} ${selectedUser.manager.user?.last_name}`} icon="fa-user-tie" variant="info" />
                    </div>
                  )}
                  {selectedUser.organization_memberships?.length > 0 && (
                    <div className="col-12 mt-2">
                      <h6 className="mb-1">Organizations:</h6>
                      <div className="d-flex flex-wrap gap-1">
                        {selectedUser.organization_memberships.map((org, idx) => (
                          <RelationshipBadge key={idx} label={org.organization?.name} icon="fa-building" variant="secondary" />
                        ))}
                      </div>
                    </div>
                  )}
                </div> */}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveMembership(membership.id)} disabled={membershipLoading}>
                    <i className="fas fa-times" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="alert alert-info small mb-0">
            <i className="fas fa-info-circle me-2" />
            No organization memberships yet. Click &quot;Add&quot; to assign this user to an organization.
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesUserEditor;
